import { useTranslation } from "react-i18next";
import { Button, Icon } from "@atoms";
import VisaLogo from "../../../../assets/visa-logo.png";
import MastercardLogo from "../../../../assets/mastercard-logo.png";

const colors = require("@theme/colors") as Record<string, any>;

export type PaymentMethodCardTemplateProps = {
  title: string;
  onClick: (() => void) | undefined;
  buttonText?: string;
  icon: JSX.Element;
  iconSubtitle: string;
  hasTransactionFee: boolean;
  hasAddButton: boolean;
  data: any;
  selectedIndex?: number;
};

export const PaymentMethodCardTemplate = ({
  onClick,
  icon,
  iconSubtitle,
  data,
  selectedIndex
}: PaymentMethodCardTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const hasCards = Array.isArray(data) && data.length > 0;
  const isLoading = data === null || data === undefined;

  return (
    <div className="w-full">
      {hasCards ? (
        <div className="flex flex-col gap-3">
          {data.map((card: any, index: number) => {
            const isSelected = index === selectedIndex;
            const brand =
              card?.cardBrand?.charAt(0)?.toUpperCase() + card?.cardBrand?.slice(1) ||
              t("billing.paymentMethod.cardFallback");
            const lastFourDigits = card?.lastFourDigits ?? "----";
            const expiryMonth = String(card?.expiryMonth ?? "").padStart(2, "0");
            const expiryYear = card?.expiryYear ? String(card.expiryYear).slice(-2) : "--";
            const normalizedBrand = (card?.cardBrand ?? "").toLowerCase();
            const logoSrc = normalizedBrand.includes("visa")
              ? VisaLogo
              : normalizedBrand.includes("master")
                ? MastercardLogo
                : null;

            return (
              <div
                key={card?.paymentId ?? card?.lastFourDigits ?? index}
                className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border px-5 py-4 shadow-sm bg-white ${
                  isSelected ? "border-neutral-medium2" : "border-neutral-medium2"
                }`}
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-neutral-lightest text-neutral-darkest">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={t("billing.paymentMethod.logoAlt", { brand })}
                        className="h-[52px] w-auto max-w-[52px]"
                      />
                    ) : (
                      <Icon variant="paymentCard" size={26} color={colors.neutral.darkest} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-neutral-darkest truncate">
                      {t("billing.paymentMethod.cardEndingIn", {
                        brand,
                        lastFourDigits,
                      })}
                    </div>
                    <div className="mt-1 text-sm text-neutral-mediumDark">
                      {t("billing.paymentMethod.expiry", {
                        month: expiryMonth,
                        year: expiryYear,
                      })}
                    </div>
                  </div>
                </div>
                {onClick ? (
                  <Button
                    onClick={onClick}
                    variant="secondary"
                    label={t("billing.paymentMethod.edit")}
                    autosize
                    color="#FFFFFF"
                    hoverColor="#FFE8D6"
                    textColor={colors.primary.medium}
                    borderColor={colors.primary.medium}
                    fontWeight={600}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : isLoading ? (
        <div className="pt-2 pl-2">
          {t("billing.paymentMethod.loading")}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-6 text-neutral-dark">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-neutral-lightest text-neutral-darkest">
            {icon}
          </div>
          <div className="text-sm text-neutral-dark">{iconSubtitle}</div>
        </div>
      )}
    </div>
  );
};
